import { getInitials } from "@upsquad/shared";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getUser } from "@/src/auth/store/auth-selectors";
import { Avatar, Button } from "@/src/common/components/ui";
import { useAppSelector } from "@/src/common/redux/store";
import { getCommunities } from "@/src/community/store/community-selectors";
import { NotificationBell } from "@/src/notification/components/notification-bell";
import { GlobalSearchBar } from "@/src/search/components/global-search-bar";
import { ProfileMenu } from "./profile-menu";

interface AppHeaderProps {
	onMenuOpen?: () => void;
}

export function AppHeader({ onMenuOpen }: AppHeaderProps) {
	const { t } = useTranslation("shell");
	const user = useAppSelector(getUser);
	const communities = useAppSelector(getCommunities);
	const initials = user ? getInitials(`${user.first_name ?? ""} ${user.last_name ?? ""}`) : "";

	const currentSlug = window.location.hostname.split(".")[0];
	const currentCommunity = communities.find((c) => c.slug === currentSlug);

	return (
		<header className="flex h-16 items-center bg-white dark:bg-neutral-900 md:bg-background md:dark:bg-background">
			{onMenuOpen && (
				<button type="button" className="pl-4 md:hidden" aria-label={t("header.menu")} onClick={onMenuOpen}>
					<Avatar size="sm">
						<Avatar.Image src={currentCommunity?.image ?? undefined} alt={currentCommunity?.name ?? "Community"} />
						<Avatar.Fallback>{getInitials(currentCommunity?.name ?? "C")}</Avatar.Fallback>
					</Avatar>
				</button>
			)}

			<div className="mx-auto hidden w-full max-w-6xl items-center gap-4 px-4 md:flex">
				<div className="flex-1">
					<GlobalSearchBar />
				</div>
				<div className="flex items-center justify-end gap-3 lg:w-72">
					<NotificationBell />
					{user && <ProfileMenu />}
				</div>
			</div>

			<div className="flex-1 md:hidden" />

			<div className="flex items-center gap-2 pr-4 md:hidden">
				<Button isIconOnly variant="ghost" size="sm" aria-label={t("header.search")}>
					<Search size={20} />
				</Button>
				<NotificationBell />
				{user && (
					<Avatar size="sm" className="cursor-pointer">
						<Avatar.Image src={user.photo_url ?? undefined} alt={initials} />
						<Avatar.Fallback>{initials || user.email[0]}</Avatar.Fallback>
					</Avatar>
				)}
			</div>
		</header>
	);
}
